import { motion } from 'framer-motion';
import { ArrowRight, ShieldCheck, Award, TrendingUp } from 'lucide-react';
import { getAuthLink } from '@/lib/links';

export function TestimonialsMonday() {
  return (
    <section className="py-20 sm:py-24 bg-[#F3F4F5]">
      <div className="max-w-[1380px] mx-auto px-5 sm:px-8">
        {/* =====================================================================
            PART 1: Exact monday.com Slice 12 "Our customers achieve more"
            (Left Solid Black Split Card + Right Soft Lavender Metric Split Card)
           ===================================================================== */}
        <motion.div
          initial={false}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4 }}
          className="mb-12"
        >
          <span className="text-xs sm:text-sm font-normal text-[#323338] block mb-2">
            Proven results
          </span>
          <h2 className="text-4xl sm:text-[48px] lg:text-[56px] font-normal tracking-[-0.03em] text-[#000000] leading-[1.12]">
            Our customers achieve more
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-24">
          {/* Left Card (7 cols): Solid Black Split Quote Card (Exact monday-slice-12.png match) */}
          <div className="lg:col-span-7 rounded-[32px] bg-[#000000] text-white overflow-hidden grid grid-cols-1 sm:grid-cols-12 min-h-[380px]">
            <div className="sm:col-span-7 p-7 sm:p-10 flex flex-col justify-between">
              <div>
                <span className="text-5xl font-serif text-[#7950A2] leading-none block mb-3">
                  &ldquo;
                </span>
                <p className="text-base sm:text-[19px] font-light leading-[1.55] text-white/95">
                  When the April hail storm hit Collin County, Ashley{' '}
                  <span className="text-[#E5DEEE] font-medium">answered 62 calls before 6 AM</span>{' '}
                  while I was asleep. We{' '}
                  <span className="text-[#F7C76C] font-medium">booked 28 roof inspections</span>{' '}
                  before our competitors even turned their phones on.
                </p>
              </div>

              <div className="mt-8 pt-4 border-t border-white/15">
                <span className="text-sm font-medium text-white block">David Vance</span>
                <span className="text-xs text-white/65">
                  Owner, Apex Ridge Roofing · Dallas–Fort Worth, TX
                </span>
              </div>
            </div>

            <div className="sm:col-span-5 relative min-h-[240px] bg-[#181B20]">
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent flex flex-col justify-end p-5">
                <span className="text-xs font-bold uppercase tracking-widest text-[#F7C76C]">
                  APEX RIDGE ROOFING
                </span>
                <span className="text-[11px] text-white/80">+$48,000 first-week storm revenue</span>
              </div>
            </div>
          </div>

          {/* Right Card (5 cols): Soft Lavender Metric Split Card (Exact monday-slice-12.png match) */}
          <div className="lg:col-span-5 rounded-[32px] bg-[#F3E3FF] text-[#181B20] overflow-hidden grid grid-cols-1 sm:grid-cols-12 min-h-[380px]">
            <div className="sm:col-span-7 p-7 sm:p-9 flex flex-col justify-between">
              <div>
                <span className="inline-block px-3 py-1 rounded-full bg-white text-[11px] font-medium text-[#181B20] mb-6 shadow-2xs">
                  Satellite Takeoffs
                </span>
                <div className="text-5xl sm:text-6xl font-medium tracking-[-0.04em] text-[#000000] mb-2">
                  90 sec
                </div>
                <p className="text-sm text-[#323338] leading-relaxed">
                  to generate a 3D aerial roof takeoff vs. waiting 24 hours and paying $45/report.
                </p>
              </div>

              <div className="mt-8">
                <a
                  href={getAuthLink('/register')}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#000000] hover:text-[#7950A2] transition-colors mb-4"
                >
                  <span>Start your free trial</span>
                  <ArrowRight className="size-3.5" />
                </a>
                <div className="pt-3 border-t border-black/10">
                  <span className="text-xs font-semibold text-[#000000] block">
                    Sarah Lindqvist
                  </span>
                  <span className="text-[11px] text-[#323338]">
                    Operations Director, Summit Peak Contracting
                  </span>
                </div>
              </div>
            </div>

            <div className="sm:col-span-5 relative min-h-[220px] bg-[#E5DEEE]">
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent flex items-end p-4">
                <span className="text-xs font-bold text-white tracking-wider uppercase">
                  SUMMIT PEAK
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* =====================================================================
            PART 2: Exact monday.com Slice 13 "Trusted by enterprises" 3-Card Row
           ===================================================================== */}
        <div className="bg-white rounded-[36px] p-8 sm:p-12 lg:p-16">
          <h3 className="text-3xl sm:text-[44px] font-normal tracking-[-0.03em] text-[#000000] leading-[1.15] max-w-xl mb-12">
            Trusted by top contractors.
            <br />
            Built for enterprise scale.
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Card 1: Enterprise-grade security */}
            <div className="rounded-[28px] border border-gray-200 p-7 flex flex-col justify-between min-h-[290px]">
              <div>
                <h4 className="text-xl font-medium text-[#000000] mb-2">
                  Enterprise-grade security
                </h4>
                <p className="text-sm text-[#323338] leading-relaxed">
                  Tenant-isolated data, encrypted homeowner records, and protected Stripe milestone
                  escrow accounts.
                </p>
              </div>

              <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 pt-6">
                {['TLS', 'Row-level isolation', 'Role access', 'Audit trail'].map((badge) => (
                  <div
                    key={badge}
                    className="min-h-16 rounded-2xl border border-gray-200 bg-[#FAFAFC] flex flex-col items-center justify-center p-2 text-center"
                  >
                    <ShieldCheck className="size-4 text-[#7950A2] mb-0.5" />
                    <span className="text-[9px] font-semibold text-[#181B20]">{badge}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Card 2: Leader in 3 Categories */}
            <div className="rounded-[28px] border border-gray-200 p-7 flex flex-col justify-between min-h-[290px]">
              <div>
                <h4 className="text-xl font-medium text-[#000000] mb-2">
                  6 Specialized Trade Agents
                </h4>
                <p className="text-sm text-[#323338] leading-relaxed">
                  Purpose-built for residential replacement, storm restoration, and commercial TPO
                  estimating.
                </p>
              </div>

              <div className="pt-6 flex items-center gap-3">
                <div className="size-14 rounded-2xl bg-[#F3E3FF] text-[#7950A2] flex items-center justify-center shrink-0">
                  <Award className="size-7" />
                </div>
                <div>
                  <span className="text-sm font-semibold text-[#000000] block">
                    #1 Roofing AI Work OS
                  </span>
                  <span className="text-xs text-[#676879]">
                    Voice + 3D Aerial + Escrow in one platform
                  </span>
                </div>
              </div>
            </div>

            {/* Card 3: 346% ROI */}
            <div className="rounded-[28px] border border-gray-200 p-7 flex flex-col justify-between min-h-[290px]">
              <div>
                <h4 className="text-xl font-medium text-[#000000] mb-2">
                  346% ROI in storm season
                </h4>
                <p className="text-sm text-[#323338] leading-relaxed">
                  Capturing just one extra $14,000 roof replacement pays for your entire AI agent
                  fleet for the year.
                </p>
              </div>

              <div className="pt-6 flex items-baseline justify-between">
                <span className="text-5xl font-medium tracking-[-0.04em] text-[#7950A2]">346%</span>
                <span className="inline-flex items-center gap-1 text-xs font-medium text-[#008A50] bg-[#00C875]/15 px-3 py-1 rounded-full">
                  <TrendingUp className="size-3.5" /> Verified Payback
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
